import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../store/slices/authSlice';
import { selectCartCount } from '../store/slices/cartSlice';
import { toggleDarkMode } from '../store/slices/uiSlice';
import api from '../api';

const FALLBACK_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇨🇦', dir: 'ltr', active: true },
  { code: 'fa', name: 'Persian', native: 'فارسی',   flag: '🇮🇷', dir: 'rtl', active: true },
];

// Icons (inline SVG for zero dependencies)
const Icon = {
  Menu: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  Close: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Cart: ({ count }) => (
    <span className="relative inline-flex">
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      {count > 0 && (
        <span className="absolute -top-2 -end-2 w-5 h-5 text-xs flex items-center justify-center
                         bg-brand-500 text-white rounded-full font-bold animate-bounce-soft">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  ),
  User: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Moon: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  ),
  Sun: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Globe: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { token, user, role } = useSelector(s => s.auth);
  const cartCount = useSelector(selectCartCount);
  const { darkMode } = useSelector(s => s.ui);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [currency, setCurrency] = useState('CAD');

  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  const isAdmin = role === 'admin';
  const isCooperatore = role === 'cooperatore';
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Fetch active languages from API
  useEffect(() => {
    api.get('/languages')
      .then(r => { if (r.data && r.data.length > 0) setLanguages(r.data); })
      .catch(() => {});
  }, []);

  // Fetch store currency from settings
  useEffect(() => {
    api.get('/settings')
      .then(r => { if (r.data?.store_currency) setCurrency(r.data.store_currency); })
      .catch(() => {});
  }, []);

  // Scroll effect
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    setLangMenuOpen(false);
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/products', label: t('nav.products') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300
      ${scrolled
        ? 'bg-white/95 dark:bg-gray-950/95 backdrop-blur-md shadow-lg'
        : 'bg-white dark:bg-gray-950 shadow-sm'
      }`}>
      <div className="container-main px-4">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.jpg" alt="NuttyMilk" className="h-12 w-auto rounded-xl object-contain" />
            {/* <span className="font-display font-bold text-xl text-nutbrown-800 dark:text-brand-400">
              Tinan<span className="text-brand-500">zar</span>
            </span> */}
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400 font-medium ms-1 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
              <span>💰</span> <span>{currency}</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${isActive(link.to)
                    ? 'text-brand-600 bg-brand-50 dark:text-brand-400 dark:bg-gray-800'
                    : 'text-gray-600 hover:text-brand-600 hover:bg-brand-50 dark:text-gray-300 dark:hover:text-brand-400 dark:hover:bg-gray-800'
                  }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">

            {/* Dark mode */}
            <button
              onClick={() => dispatch(toggleDarkMode())}
              className="btn-ghost p-2 rounded-full"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Icon.Sun /> : <Icon.Moon />}
            </button>

            {/* Language switcher — dropdown */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="btn-ghost px-3 py-2 rounded-full flex items-center gap-1.5 text-sm font-medium"
                aria-haspopup="true"
                aria-expanded={langMenuOpen}
              >
                <span>{currentLang.flag}</span>
                <span className="hidden sm:inline">{currentLang.label || currentLang.code?.toUpperCase()}</span>
                <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${langMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langMenuOpen && (
                <div className="absolute end-0 top-full mt-2 w-44 card shadow-xl py-1.5 z-50">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full text-start px-4 py-2.5 text-sm flex items-center gap-3 transition-colors
                        hover:bg-brand-50 dark:hover:bg-gray-800
                        ${i18n.language === lang.code
                          ? 'text-brand-600 dark:text-brand-400 font-semibold bg-brand-50/60 dark:bg-gray-800/60'
                          : 'text-gray-700 dark:text-gray-300'
                        }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="flex-1">{lang.native || lang.name || lang.code}</span>
                      {i18n.language === lang.code && (
                        <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" className="btn-ghost p-2 rounded-full">
              <Icon.Cart count={cartCount} />
            </Link>

            {/* User menu */}
            {token ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-brand-50 hover:bg-brand-100 dark:bg-gray-800 dark:hover:bg-gray-700 transition-all"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-nutbrown-600 flex items-center justify-center text-white text-xs font-bold">
                    {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.first_name || user?.username || t('nav.profile')}
                  </span>
                </button>
                {userMenuOpen && (
                  <div className="absolute end-0 top-full mt-2 w-48 card shadow-xl py-1 z-50">
                    <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-gray-800">
                      <Icon.User /> {t('nav.profile')}
                    </Link>
                    <Link to="/orders" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-gray-800">
                      📦 {t('nav.orders')}
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-gray-800 font-medium">
                        ⚙️ {t('nav.admin')}
                      </Link>
                    )}
                    {isCooperatore && (
                      <Link to="/cooperatore" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-gray-800 font-medium">
                        🏪 Shop Panel
                      </Link>
                    )}
                    <hr className="my-1 border-gray-100 dark:border-gray-700" />
                    <button onClick={handleLogout} className="w-full text-start flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800">
                      🚪 {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="btn-ghost text-sm">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="btn-primary text-sm px-4 py-2">
                  {t('nav.register')}
                </Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden btn-ghost p-2 rounded-full"
            >
              {mobileOpen ? <Icon.Close /> : <Icon.Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 dark:border-gray-800 py-4 space-y-1 animate-fade-in-up">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all
                  ${isActive(link.to)
                    ? 'text-brand-600 bg-brand-50 dark:text-brand-400 dark:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
              >
                {link.label}
              </Link>
            ))}
            {!token && (
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="flex-1 text-center py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="flex-1 text-center py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium">
                  {t('nav.register')}
                </Link>
              </div>
            )}
            {isAdmin && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-600 font-medium hover:bg-brand-50 dark:hover:bg-gray-800 rounded-lg">
                ⚙️ {t('nav.admin')}
              </Link>
            )}
            {isCooperatore && (
              <Link to="/cooperatore" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-600 font-medium hover:bg-brand-50 dark:hover:bg-gray-800 rounded-lg">
                🏪 Shop Panel
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
